/**
 * Segment instrumentation for the Select field's bulk "Add options in bulk"
 * flow. Kept next to the Select-specific wrapper rather than inside
 * `BulkAddListItemsModal`, so the modal stays a generic list control.
 *
 * `userId` is not sent as a property: the Designer's AnalyticsEngine calls
 * `identifyUser` once per session, so Segment attaches the user to every
 * subsequent `track` call.
 */
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import {trackEvent} from '@packages/utilities/analytics';

import type {BulkListItem} from './parseBulkListItems';

/**
 * How the paste was formatted. The parser picks a delimiter per line, so a
 * paste that mixes both is reported as `tab` — accurate enough for the question
 * this answers (are people pasting out of a spreadsheet?). A paste with neither
 * delimiter is `none` rather than `comma`: those are single-column lists, which
 * the parser handles by reusing each line as its own value.
 */
const separatorTypeFor = (rawText: string) => {
  if (rawText.includes('\t')) return 'tab';
  return rawText.includes(',') ? 'comma' : 'none';
};

/**
 * Bounds on the sampled `rawOptionsAdded` list. A paste is unbounded but a
 * Segment `track` call is capped at 32KB, so the sample is limited by both
 * option count and total size. The character budget matters on its own because
 * a handful of very long options can outweigh hundreds of short ones.
 */
const RAW_OPTIONS_MAX_COUNT = 250;
const RAW_OPTIONS_MAX_CHARS = 10_000;

/**
 * The prefix of `items` that fits within both bounds. `numOptionsAdded` always
 * reports the true total, so `rawOptionsTruncated` is what tells analysis that
 * the recorded list is only a sample.
 */
const sampleRawOptions = (items: ReadonlyArray<BulkListItem>) => {
  const rawOptionsAdded: Array<BulkListItem> = [];
  let chars = 0;

  for (const {text, value} of items) {
    if (rawOptionsAdded.length >= RAW_OPTIONS_MAX_COUNT) break;
    chars += text.length + value.length;
    if (chars > RAW_OPTIONS_MAX_CHARS) break;
    rawOptionsAdded.push({text, value});
  }

  return {
    rawOptionsAdded,
    rawOptionsTruncated: rawOptionsAdded.length < items.length,
  };
};

export const trackBulkSelectOptionsModalOpened = (siteId: string) => {
  trackEvent({
    name: ANALYTICS_EVENTS.FORM_SELECT_BULK_ADD_MODAL_OPENED,
    data: {siteId},
  });
};

export const trackBulkSelectOptionsAdded = ({
  siteId,
  items,
  rawText,
}: {
  siteId: string;
  items: ReadonlyArray<BulkListItem>;
  rawText: string;
}) => {
  trackEvent({
    name: ANALYTICS_EVENTS.FORM_SELECT_BULK_OPTIONS_ADDED,
    data: {
      siteId,
      numOptionsAdded: items.length,
      separatorType: separatorTypeFor(rawText),
      ...sampleRawOptions(items),
    },
  });
};
